import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { ImageService } from '@epicuro-next/ataraxia/image';
import { IImage, IImageBreakpoint } from '@epicuro-next/ataraxia/models';
import { ProductMedia } from 'graphql-api/gql-api-interfaces';

import { CarouselBase } from '../carousel-base';
import { carouselAnimations } from '../carousel.animation';

/**
 * Component to show a carousel of media elements
 */
@Component({
  selector: 'atx-carousel-card',
  templateUrl: './carousel-card.component.html',
  styleUrls: ['./carousel-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [carouselAnimations],
  standalone: false,
})
export class CarouselCardComponent
  extends CarouselBase<IImage>
  implements OnChanges, OnInit
{
  @Input()
  public imageBreakpoints: IImageBreakpoint[] = [];

  @Input()
  public carouselBreakpoints: IImageBreakpoint[] = [];

  @Input()
  public maxWidth?: number;

  /** Input used to provide a placeholder image to the carousel */
  @Input()
  public placeholderImage: string;

  @Input()
  public fullSpace = true;

  @Input()
  public useThumbnailsSkeleton = true;

  /** Input used to show/hide the thumbnails */
  @Input()
  public thumbnailsVisible = true;

  @Input()
  public progressiveImageLoad = true;

  /** Input used to apply optionally border radius to the main image */
  @Input()
  public borderRadius = false;

  @Input()
  public initialBodyImageUrl: string;

  @Input()
  public mayUseMissingImageContent = false;

  /** Output which emits when the main image is clicked */
  @Output()
  public clickedMainImage: EventEmitter<boolean> = new EventEmitter();

  @Output()
  public toggledHover: EventEmitter<boolean> = new EventEmitter();

  @Input()
  public zoomFactorThubmailMissingImage = 1;

  @Input()
  public currentVariantMedia: ProductMedia[];

  @Input() public missingImageUrlSignatures: string[] = [
    'default.jpg',
    'placeholder-image.jpg',
  ];

  public mainImageLoaded = false;
  public mainImageFailedToLoad = false;

  public hoverToggleImage = false;
  private indicatorsSubset?: IImage[];

  private thumbnailFailedToLoad: { [key: number]: boolean } = {};

  /** Binding for the class name */
  @HostBinding('class') public componentClass = 'atx-carousel-card';

  @Input() public missingImageTemplate: TemplateRef<unknown>;

  public get indicators() {
    if (!this.indicatorsSubset) {
      this.indicatorsSubset = this.getIndicators();
    }
    return this.indicatorsSubset;
  }

  public get loadingMainImageFinished(): boolean {
    return (
      this.mainImageLoaded ||
      this.mainImageFailedToLoad ||
      this.useMissingImageContent
    );
  }

  public get showThumbnailsSkeleton(): boolean {
    return this.useThumbnailsSkeleton && !this.loadingMainImageFinished;
  }

  public get bodyImage() {
    if (this.currentIndex < 0 && this.initialBodyImageUrl) {
      return this.initialBodyImageUrl;
    }
    return this.hoverToggleImage
      ? this.currentVariantMedia[1]?.url ?? this.currentItem?.url
      : this.currentItem?.url;
  }

  public get useMissingImageContent(): boolean {
    if (!this.mayUseMissingImageContent) {
      return false;
    }

    return (
      this.mainImageFailedToLoad ||
      this.imageUrlIndicatesImageIsMissing(this.bodyImage)
    );
  }

  /**
   * Thumbnails breakpoint for image resizer
   * @private
   */
  private thumbnailBreakpoints: IImageBreakpoint = {
    mediaMinWidth: '40px',
    maxWidth: 45,
    maxHeight: 45,
  };

  /**
   * Component constructor
   * @param imageService - image service
   */
  constructor(private imageService: ImageService) {
    super();
  }

  public onMainImageMouseEnter() {
    this.toggledHover.emit(true);
    this.hoverToggleImage = true;
  }

  public onMainImageMouseLeave() {
    this.toggledHover.emit(false);
    this.hoverToggleImage = false;
  }

  public ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  /**
   * Returns the array of visible media, based on the current position of the carousel and amount of thumbnails configured to be shown
   */
  public getIndicators() {
    return this.items?.slice(this.offSet, this.offSet + this.thumbnailsNumber);
  }

  protected override offsetChanged(): void {
    this.indicatorsSubset = undefined;
  }

  /**
   * emits when the main image is clicked
   */
  public clickedImage() {
    return this.clickedMainImage.emit(true);
  }

  public onMainImageLoaded() {
    this.mainImageLoaded = true;
  }

  public onMainImageError() {
    this.mainImageFailedToLoad = true;
  }

  /** Add image resizer query parameters to url */
  public getThumbSrcSet(imgSrc: string): string {
    return this.imageService.getSrcSet(imgSrc, this.thumbnailBreakpoints);
  }

  public ngOnInit(): void {
    if (this.initialBodyImageUrl) {
      // force not to preselect any images
      this.updateIndexes(-1);
    }
  }

  public onThumbnailImageLoaded(index: number): void {
    this.thumbnailFailedToLoad[index] = false;
  }

  public onThumbnailImageError(index: number): void {
    this.thumbnailFailedToLoad[index] = true;
  }

  public useMissingImageContentForThumbnail(index: number): boolean {
    if (!this.mayUseMissingImageContent) {
      return false;
    }

    const thumbnailImageUrl = this.indicators[index]?.url;

    return (
      Boolean(this.thumbnailFailedToLoad[index]) ||
      this.imageUrlIndicatesImageIsMissing(thumbnailImageUrl)
    );
  }

  private imageUrlIndicatesImageIsMissing(url: string): boolean {
    if (url.endsWith(this.placeholderImage)) {
      return true;
    }

    return this.missingImageUrlSignatures.some((signature) => {
      return url.endsWith(signature);
    });
  }
}
